import { base64ToBytes, bytesToBase64 } from '@/utils/bytes-base64'

// AHA-47221: "this window is an Office dialog, not the task pane."
//
// messageParent is the dialog's entire Office API surface — it is how a dialog talks to the pane
// that opened it, and it is the pane that owns the deck. A dialog has no Rich API and no
// document.settings, so anything that touches the deck (inserting slides, clearing a binding)
// has to be handed to the pane rather than attempted here.
//
// Shared rather than defined per consumer: the selection bar and the editor's Back button both
// change what they do in a dialog, and they have to agree on what a dialog is.
export const isPptDialog = () => {
  return typeof window.Office?.context?.ui?.messageParent === 'function'
}

// What a dialog can say to the pane. Every message crosses the boundary as a JSON string, so the
// type is what tells the pane which one it is holding — the add-slides payload predates this and
// is still recognised by its own shape (see PptAddinCanvasMenu).
export const PPT_DIALOG_MESSAGE_TYPES = {
  CLOSE: 'close',
  ADD_PRESENTATION: 'add-presentation',
  DIRTY: 'dirty',
}

// AHA-48164: "Add N slides", with each slide's sidebar thumbnail riding along as
// `snapshots: { [slideId]: base64 PNG }` so the pane can set the new PowerPoint slides' add-in
// snapshots. Only the dialog has the editor sidebar to capture from, and the pane does the insert,
// so the pictures have to cross the boundary.
//
// The pictures are cosmetic and the Add is not. A picture that cannot be encoded is left out, and
// a message that will not go with the pictures goes again without them, exactly as it did before
// snapshots existed. Only a send that fails without them throws, as it always has.
export const sendPptDialogAddSlides = ({ presentationId, slideIds, pngBySlideId }) => {
  const message = { presentationId, slideIds }
  const snapshots = {}
  Object.keys(pngBySlideId || {}).forEach((slideId) => {
    try {
      snapshots[slideId] = bytesToBase64(pngBySlideId[slideId])
    } catch (error) {
      // Nothing to do: the slide keeps the snapshot PowerPoint takes itself.
    }
  })
  if (Object.keys(snapshots).length) {
    try {
      window.Office.context.ui.messageParent(JSON.stringify({ ...message, snapshots }))
      return
    } catch (error) {
      // Falls through to the Add without pictures.
    }
  }
  window.Office.context.ui.messageParent(JSON.stringify(message))
}

// The pane's half of the above: the pictures back as bytes, keyed by slide id. Untrusted like every
// dialog message, so anything malformed is dropped, and so is a picture for a slide the message is
// not adding; never throws, so a bad picture cannot cost the Add. Whether the bytes are really a
// PNG is left to the patcher, which refuses anything else (services/office/addin-snapshot-patch).
export const readPptDialogSlideSnapshots = (payload) => {
  const pngBySlideId = {}
  const snapshots = payload?.snapshots
  if (!snapshots || typeof snapshots !== 'object' || !Array.isArray(payload.slideIds)) {
    return pngBySlideId
  }
  payload.slideIds.forEach((id) => {
    const slideId = String(id)
    const png = Object.prototype.hasOwnProperty.call(snapshots, slideId) ? snapshots[slideId] : null
    if (typeof png !== 'string' || !png) return
    try {
      pngBySlideId[slideId] = base64ToBytes(png)
    } catch (error) {
      // Not base64: this slide keeps the snapshot PowerPoint takes itself.
    }
  })
  return pngBySlideId
}

// "Close me." A dialog cannot close itself — Office.context.ui is the child half of the API in
// there, so the only handle to this window lives in the pane that opened it. The Close button
// therefore asks rather than acts, and the pane does it (and re-reads the presentation if this
// window reported a change, the same as it does when the user closes the window by its own
// chrome).
//
// No-op outside a dialog, so a caller can be rendered anywhere without guarding twice.
export const requestPptDialogClose = () => {
  if (!isPptDialog()) return
  window.Office.context.ui.messageParent(JSON.stringify({ type: PPT_DIALOG_MESSAGE_TYPES.CLOSE }))
}

// "Something in here was saved, and this is the presentation it was saved to."
//
// The pane re-reads the presentation when this dialog closes, because the dialog is a separate
// browser context with its own store and the pane's copy does not learn what happened in here.
// That re-read is only worth paying for if something actually changed, and this window is the
// only one that can know: it is the one holding the editor. WHAT changed is still not said — the
// answer to any of it is the same re-read — but WHICH PRESENTATION is, because the pane is not
// the only stale window and the others are not all bound to the same presentation (see
// business-logic/ppt-presentation-sync).
//
// The id matters here in particular because the dialog is a whole editor: Back inside it goes to
// the presentation LIST, so the presentation being edited when this fires is not necessarily the
// one the pane is presenting.
//
// Sent once per presentation per dialog window: the Set is module state, and a dialog window
// lives exactly as long as the dialog does, so it resets by being a new window. Without the guard
// every autosave in a long editing session would cross the boundary to re-say the one thing the
// pane already knows; keyed by id rather than a single flag so editing a second presentation in
// the same window is still reported.
//
// No-op outside a dialog, so a caller can be rendered anywhere without guarding twice.
const dirtyNotifiedPresentationIds = new Set()

export const notifyPptDialogDirty = (presentationId) => {
  if (!isPptDialog()) return
  const id = Number(presentationId)
  if (!Number.isFinite(id) || id <= 0) return
  if (dirtyNotifiedPresentationIds.has(id)) return
  dirtyNotifiedPresentationIds.add(id)
  window.Office.context.ui.messageParent(JSON.stringify({
    type: PPT_DIALOG_MESSAGE_TYPES.DIRTY,
    presentationId: id,
  }))
}

// "Take the whole presentation as one PowerPoint slide."
//
// The payload carries nothing but the presentation id, because that is the entire input to the
// flow it asks for: bindWholePresentationToPowerpoint reads an id and nothing else. No slide
// ids, deliberately — a whole-presentation binding is the opposite of a slide selection, and
// the pane tells the two messages apart by their type rather than by guessing from a missing
// field.
//
// Same reason as requestPptDialogClose: the work needs document.settings and an add-in frame,
// neither of which exists inside a dialog, so the dialog can only ask.
//
// No-op outside a dialog, so a caller can be rendered anywhere without guarding twice.
export const requestPptAddPresentation = (presentationId) => {
  if (!isPptDialog()) return
  window.Office.context.ui.messageParent(JSON.stringify({
    type: PPT_DIALOG_MESSAGE_TYPES.ADD_PRESENTATION,
    presentationId,
  }))
}
